import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from "@mui/material";
import { loadFromLocalStorage } from '../../Common/Validation/LocalStorage';
import { TableColumn } from 'react-data-table-component';
import DataGrid from '../../Component/DataGrid/DataGrid';
import { SyncContext, SyncContextType } from '../../Contexts/BatchSyncContext';
import { SlideUpTransition } from '../../Component/Reusables/Transitions';
import ReferralService from '../../Services/ReferralService';
import moment from 'moment';

interface InvitedUser {
    Name: string | null;
    ReferrerBonus: number | null;
    ReferredDate: string | null;
    Message: string;
    MobileNumber: string;
    currencySymbol: string;
}

interface InvitedListProps {
    open: boolean;
    onClose?: (refresh: boolean, action: string) => void;
    selectedReferrerId: string;
    accessToken: string
    refreshData: () => void;
}

interface InvitedListState {
    accessToken: string;
    invitedUsers: InvitedUser[];
    currentPage: number;
    currentRowsPerPage: number;
    totalRows: number;
    searchText: string;
    selectedFilter: string;
    filterValue: string;
    isLoading: boolean;
}

class InvitedList extends React.Component<InvitedListProps, InvitedListState> {
    static contextType = SyncContext;
    context!: SyncContextType;

    private tableColumns: TableColumn<InvitedUser>[] = [
        {
            name: 'Name',
            selector: row => row.Name || '-',
            sortable: true,
            minWidth: '150px',
            wrap: true,
            id: 'Name'
        },
        {
            name: 'Mobile Number',
            selector: row => row.MobileNumber || '-',
            sortable: true,
            minWidth: '150px',
            id: 'MobileNumber'
        },
        {
            name: 'Referrer Bonus',
            selector: row => row.ReferrerBonus ? `${row.currencySymbol ?? "$"} ${row.ReferrerBonus}` : '-',
            sortable: true,
            right: true,
            minWidth: "200px",
            id: 'ReferrerBonus'
        },
        {
            name: 'Status',
            selector: row => row.Message || '-',
            sortable: true,
            minWidth: '250px',
            wrap: true,
            center: true,
            id: 'Message'
        },
        {
            name: 'Referred Date',
            selector: row => row.ReferredDate ? moment(row.ReferredDate).format('DD MMM YYYY') : '-',
            sortable: true,
            minWidth: "150px",
            id: 'ReferredDate'
        },
    ];

    constructor(props: InvitedListProps) {
        super(props);
        this.state = {
            accessToken: '',
            invitedUsers: [],
            currentPage: 1,
            currentRowsPerPage: 10,
            totalRows: 0,
            searchText: '',
            selectedFilter: '',
            filterValue: '',
            isLoading: false,
        };
    }

    async componentDidMount() {
        try {
            const accessToken_ = await loadFromLocalStorage('accessToken');
            if (!accessToken_) {
                localStorage.clear();
                window.location.href = '/';
                return;
            }

            this.setState({ accessToken: accessToken_.toString() }, () => {
                this.getInvitedUsers();
            });
        } catch (error) {
            console.error('Error loading access token:', error);
        }
    }

    private getInvitedUsers = async () => {
        const { accessToken, currentPage, currentRowsPerPage, searchText, selectedFilter, filterValue } = this.state;
        const { selectedReferrerId } = this.props;

        const pageIndex = currentPage - 1;

        let pagination: any = {
            pageNumber: pageIndex,
            recordPerPage: currentRowsPerPage
        };

        if (searchText) {
            pagination["searchText"] = searchText;
        }

        if (selectedFilter) {
            pagination[selectedFilter] = filterValue;
        }

        this.setState({ isLoading: true });

        try {
            const response = await ReferralService.getInvitedUsersList(
                accessToken,
                pagination,
                selectedReferrerId
            );

            if (response?.ok) {
                const result = await response.json();
                this.setState({
                    invitedUsers: result.data || [],
                    totalRows: result.totalCount || 0
                });
            }
        } catch (error) {
            console.error("Error fetching invited users list:", error);
        } finally {
            this.setState({ isLoading: false });
        }
    };

    handlePageChange = (page: number) => {
        this.setState({ currentPage: page }, this.getInvitedUsers);
    };

    handleRowsPerPageChange = (rows: number, page: number) => {
        this.setState({
            currentRowsPerPage: rows,
            currentPage: page
        }, this.getInvitedUsers);
    };

    handleSearch = (text: string) => {
        this.setState({
            searchText: text,
            currentPage: 1
        }, this.getInvitedUsers);
    };

    handleFilterChange = (selectedFilter: string, filterValue: string) => {
        this.setState({
            selectedFilter,
            filterValue,
            currentPage: 1
        }, this.getInvitedUsers);
    };

    render() {
        const { open, onClose } = this.props;
        const { invitedUsers, isLoading, totalRows, currentPage, currentRowsPerPage } = this.state;

        return (
            <Dialog
                open={open}
                onClose={() => onClose?.(false, "cancel")}
                fullWidth
                keepMounted
                maxWidth="lg"
                TransitionComponent={SlideUpTransition}
            >
                <DialogContent className="subMenuModal" style={{ display: 'flex', flexDirection: 'column', height: '80vh' }}>
                    <DataGrid
                        title="Invited Users"
                        search="Users"
                        defaultSearch={this.state.searchText}
                        onSearch={this.handleSearch}
                        onFilterChange={this.handleFilterChange}
                        columns={this.tableColumns}
                        data={invitedUsers}
                        onChangePage={this.handlePageChange}
                        onChangeRowsPerPage={this.handleRowsPerPageChange}
                        pagination
                        paginationTotalRows={totalRows}
                        paginationDefaultPage={currentPage}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => onClose?.(false, "cancel")}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default InvitedList;